import { FileText, Hammer, Zap, ShieldCheck, Mail, Info } from 'lucide-react';

export default function Terms() {
  const points = [
    {
      title: 'Service & AI Usage',
      icon: Zap,
      color: 'green',
      content: `DIYElectronicMech provides an AI-powered electronics diagnostic, component search, and project platform. Use the 'ElectrAI' assistant at your own risk. Electronics repair and building carries inherent risks including high voltage, short circuits, and potential fires. Always follow safety best-practices.`
    },
    {
      title: 'Account Responsibilities',
      icon: ShieldCheck,
      color: 'cyan',
      content: `If you create an account, you are responsible for maintaining its security. Pro subscriptions are for a single user; do not share accounts. We reserve the right to suspend any account that violates our fair-usage policies.`
    },
    {
      title: 'Subscription & Refunds',
      icon: FileText,
      color: 'amber',
      content: `Pro and Premium subscriptions are billed in advance on a monthly or annual basis. We offer no-questions-asked refunds within the first 7 days of your first subscription payment. Manage your payments anytime via Stripe Billing in Settings.`
    },
    {
      title: 'Content & Projects',
      icon: Hammer,
      color: 'purple',
      content: `Projects provided on the platform are for educational and inspirational purposes. We do not guarantee the results or safety of any DIY build. Always consult multiple sources and use proper protective equipment.`
    }
  ];

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', padding: '0 1.5rem 3rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{ padding: '2.5rem 0 2rem', borderBottom: '1px solid var(--border-color)', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2.2rem', fontWeight: 800, marginBottom: '0.4rem' }}>
            Terms of <span style={{ color: 'var(--color-secondary)' }}>Service</span>
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {/* Introduction */}
        <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '2.5rem' }}>
          Welcome to <span style={{ color: 'var(--color-secondary)', fontWeight: 600 }}>DIYElectronicMech</span>. By using our platform, you agree to these terms. Please read them carefully. We're here to provide the best AI tools for makers, but you're responsible for how you use them.
        </p>

        {/* Grid Blocks */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '1.25rem' }}>
          {points.map(point => {
            const colors: Record<string, string> = {
              green: 'var(--color-primary)',
              cyan: 'var(--color-secondary)',
              purple: 'var(--color-purple)',
              amber: 'var(--color-accent)'
            };
            const color = colors[point.color];

            return (
              <div key={point.title} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
                  <div style={{ width: '36px', height: '36px', background: `${color}15`, border: `1px solid ${color}33`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <point.icon size={18} color={color} />
                  </div>
                  <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>{point.title}</h2>
                </div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.8 }}>
                  {point.content}
                </p>
              </div>
            );
          })}
        </div>

        {/* Detailed Points */}
        <div style={{ marginTop: '3rem', fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.8, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
            <Info size={16} color="var(--color-secondary)" style={{ marginTop: '2px', flexShrink: 0 }} />
            <p>We reserve the right to modify these terms at any time. Continued use of the platform after changes constitutes acceptance of the new terms.</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
            <Mail size={16} color="var(--color-secondary)" style={{ marginTop: '2px', flexShrink: 0 }} />
            <p>For official legal correspondence, please email <span style={{ color: 'var(--color-secondary)' }}>legal@diy-mech.com</span> or mail to DIY-MECH, 123 Maker Way, Tech City, 10101, US.</p>
          </div>
        </div>

      </div>
    </div>
  );
}
